import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import NotFoundPage from './NotFoundPage';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sparkles, MapPin, Loader2, BadgeCheck, Star, Clock, Users, ChevronRight, Home, Crown, DollarSign } from 'lucide-react';
import { API } from '../utils/api';


const WORKSHOP_TYPE_LABELS = {
  'teatro': 'Teatro',
  'meditacion': 'Meditación',
  'baile': 'Baile',
  'yoga': 'Yoga',
  'arte': 'Arte',
  'musica': 'Música',
  'deportes': 'Deportes',
  'mindfulness': 'Mindfulness',
  'terapia-grupal': 'Terapia Grupal',
  'escritura': 'Escritura',
  'danza': 'Danza',
  'trekking': 'Trekking'
};

function StarRating({ rating, size = 'sm' }) {
  const sizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star 
          key={star} 
          className={`${sizeClass} ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600'}`} 
        />
      ))}
    </div>
  );
}

function Breadcrumbs({ workshopType, typeLabel, location, locationLabel }) {
  return (
    <nav className="flex items-center gap-2 text-sm text-slate-400 mb-6 flex-wrap">
      <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
        <Home className="w-4 h-4" />
        <span>Inicio</span>
      </Link>
      <ChevronRight className="w-4 h-4" />
      <Link to="/talleres" className="hover:text-emerald-400 transition-colors">
        Talleres
      </Link>
      {workshopType && (
        <>
          <ChevronRight className="w-4 h-4" />
          {location ? (
            <Link to={`/taller/${workshopType}`} className="hover:text-emerald-400 transition-colors">
              {typeLabel}
            </Link>
          ) : (
            <span className="text-slate-200">{typeLabel}</span>
          )}
        </>
      )}
      {location && (
        <>
          <ChevronRight className="w-4 h-4" />
          <span className="text-slate-200">{locationLabel}</span>
        </>
      )}
    </nav>
  );
}

function WorkshopCard({ workshop }) {
  return (
    <Link to={`/talleres/${workshop.workshop_id}`}>
      <Card className="h-full bg-white border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all duration-300 group">
        <CardContent className="p-5">
          <div className="flex items-start gap-4 mb-3">
            {workshop.logo ? (
              <img 
                src={workshop.logo} 
                alt={workshop.name} 
                className="w-16 h-16 rounded-xl object-cover ring-2 ring-slate-200 group-hover:ring-emerald-500/50 transition-all duration-300" 
              />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-purple-500" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-base text-slate-800 group-hover:text-emerald-600 transition-colors duration-200">
                  {workshop.name}
                </h3>
                {workshop.is_premium && <Crown className="w-4 h-4 text-amber-500 flex-shrink-0" />}
                {workshop.is_verified && <BadgeCheck className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
              </div>
              {workshop.workshop_type && (
                <span className="text-sm text-purple-600 font-medium">{workshop.workshop_type}</span>
              )}
            </div>
          </div>
          
          {/* Description */}
          {workshop.description && (
            <p className="text-sm text-slate-600 line-clamp-2 mb-3">{workshop.description}</p>
          )}
          
          {/* Price and modality */}
          <div className="flex flex-wrap gap-2 mb-3">
            {workshop.is_free ? (
              <span className="text-xs px-2.5 py-1 bg-green-100 text-green-800 rounded-full font-medium border border-green-200">
                Gratuito
              </span>
            ) : workshop.price && (
              <span className="flex items-center gap-1 text-xs px-2.5 py-1 bg-emerald-100 text-emerald-800 rounded-full font-medium border border-emerald-200">
                <DollarSign className="w-3 h-3" />
                ${workshop.price.toLocaleString('es-CL')}
              </span>
            )}
            {workshop.modality && (
              <span className="text-xs px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full font-medium border border-blue-200">
                {workshop.modality}
              </span>
            )}
          </div>
          
          {/* Schedule and duration */}
          <div className="flex flex-wrap gap-3 text-xs text-slate-500 mb-3">
            {workshop.schedule && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />{workshop.schedule}
              </span>
            )}
            {workshop.duration && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />{workshop.duration}
              </span>
            )}
          </div>
          
          {/* Rating */}
          {workshop.average_rating > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <StarRating rating={Math.round(workshop.average_rating)} size="sm" />
              <span className="text-sm text-slate-500">({workshop.total_reviews})</span>
            </div>
          )}
          
          {/* Location */}
          {workshop.city && (
            <div className="flex items-center gap-1 text-sm text-slate-500 mt-2">
              <MapPin className="w-4 h-4" />{workshop.city}
              {workshop.region && <span className="text-slate-400">• {workshop.region}</span>}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

function RelatedSearches({ workshopType, location, filters }) {
  if (!filters) return null;
  
  const { types = [], cities = [] } = filters;
  
  const otherTypes = types.filter(t => t.slug !== workshopType).slice(0, 6);
  const otherCities = workshopType ? cities.filter(c => c.slug !== location).slice(0, 8) : [];
  
  return (
    <div className="mt-12 space-y-8">
      {location && otherTypes.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-4">
            Otros tipos de talleres en {filters.cities?.find(c => c.slug === location)?.label || location}
          </h3>
          <div className="flex flex-wrap gap-2">
            {otherTypes.map((t) => (
              <Link
                key={t.slug}
                to={`/taller/${t.slug}/${location}`}
                className="px-4 py-2 bg-slate-800 hover:bg-purple-500/20 border border-slate-700 hover:border-purple-500/50 rounded-lg text-sm text-slate-300 hover:text-purple-400 transition-all"
              >
                {t.label} en {filters.cities?.find(c => c.slug === location)?.label || location}
              </Link>
            ))}
          </div>
        </div>
      )}
      
      {workshopType && otherCities.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-4">
            Talleres de {WORKSHOP_TYPE_LABELS[workshopType] || workshopType} en otras ciudades
          </h3>
          <div className="flex flex-wrap gap-2">
            {otherCities.map((c) => (
              <Link
                key={c.slug}
                to={`/taller/${workshopType}/${c.slug}`}
                className="px-4 py-2 bg-slate-800 hover:bg-purple-500/20 border border-slate-700 hover:border-purple-500/50 rounded-lg text-sm text-slate-300 hover:text-purple-400 transition-all"
              >
                {c.label} ({c.count})
              </Link>
            ))}
          </div>
        </div>
      )}
      
      {!workshopType && types.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-4">
            Buscar por tipo de taller
          </h3>
          <div className="flex flex-wrap gap-2">
            {types.map((t) => (
              <Link
                key={t.slug}
                to={`/taller/${t.slug}`}
                className="px-4 py-2 bg-slate-800 hover:bg-purple-500/20 border border-slate-700 hover:border-purple-500/50 rounded-lg text-sm text-slate-300 hover:text-purple-400 transition-all"
              >
                {t.label} ({t.count})
              </Link>
            ))}
          </div>
        </div>
      )}
      
      {workshopType && !location && cities.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-4">
            Talleres de {WORKSHOP_TYPE_LABELS[workshopType] || workshopType} por ciudad
          </h3>
          <div className="flex flex-wrap gap-2">
            {cities.map((c) => (
              <Link
                key={c.slug}
                to={`/taller/${workshopType}/${c.slug}`}
                className="px-4 py-2 bg-slate-800 hover:bg-purple-500/20 border border-slate-700 hover:border-purple-500/50 rounded-lg text-sm text-slate-300 hover:text-purple-400 transition-all"
              >
                {c.label} ({c.count})
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function WorkshopsByType() {
  const { workshopType } = useParams();
  const navigate = useNavigate();
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState(null);
  const [selectedCity, setSelectedCity] = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [workshopsRes, filtersRes] = await Promise.all([
          axios.get(`${API}/seo/talleres/${workshopType}?page=${page}&limit=12`),
          axios.get(`${API}/seo/talleres/filters`)
        ]);
        setWorkshops(workshopsRes.data.workshops || []);
        setTotalPages(workshopsRes.data.pages || 1);
        setTotal(workshopsRes.data.total || 0);
        setData(workshopsRes.data);
        setFilters(filtersRes.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    load();
  }, [workshopType, page]);

  const handleCityChange = (city) => {
    if (city && city !== 'all') {
      navigate(`/taller/${workshopType}/${city}`);
    }
    setSelectedCity(city);
  };

  // Validar workshopType. Si no es válido → 404 con noindex.
  if (!WORKSHOP_TYPE_LABELS[workshopType]) return <NotFoundPage />;

  const typeLabel = data?.type_label || WORKSHOP_TYPE_LABELS[workshopType] || workshopType;

  const seoTitle = `Talleres de ${typeLabel} para Rehabilitación | Sin Adicciones`;
  const seoDescription = `Encuentra talleres de ${typeLabel.toLowerCase()} especializados en rehabilitación de adicciones en Chile. ${total} talleres con reseñas y contacto directo.`;

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={`https://sinadicciones.org/taller/${workshopType}`} />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        <link rel="canonical" href={`https://sinadicciones.org/taller/${workshopType}`} />
      
      <Header />
      <main className="pt-20">
        <section className="bg-slate-900 border-b border-slate-800 py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <Breadcrumbs workshopType={workshopType} typeLabel={typeLabel} />
            <h1 className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight mb-3" data-testid="workshops-seo-title">
              Talleres de {typeLabel}
            </h1>
            <p className="text-slate-400 text-lg">
              {total > 0 ? `${total} talleres de ${typeLabel.toLowerCase()} en Chile` : `Talleres de ${typeLabel.toLowerCase()} para rehabilitación`}
            </p>
            
            {filters?.cities && filters.cities.length > 0 && (
              <div className="mt-6 flex items-center gap-3">
                <span className="text-slate-400 text-sm">Filtrar por ciudad:</span>
                <Select value={selectedCity} onValueChange={handleCityChange}>
                  <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-slate-200">
                    <MapPin className="w-4 h-4 mr-2 text-slate-500" />
                    <SelectValue placeholder="Todas las ciudades" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all" className="text-slate-300">Todas las ciudades</SelectItem>
                    {filters.cities.map((c) => (
                      <SelectItem key={c.slug} value={c.slug} className="text-slate-300">
                        {c.label} ({c.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : workshops.length === 0 ? (
              <div className="text-center py-20 bg-slate-900 rounded-xl border border-slate-800">
                <Sparkles className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <h3 className="text-xl text-slate-50 mb-2">No se encontraron talleres de {typeLabel.toLowerCase()}</h3>
                <p className="text-slate-400 mb-6">Intenta buscar otro tipo de taller</p>
                <Button asChild variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                  <Link to="/talleres">Ver todos los talleres</Link>
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="workshops-seo-grid">
                  {workshops.map((workshop) => (
                    <WorkshopCard key={workshop.workshop_id} workshop={workshop} />
                  ))}
                </div>
                
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-12">
                    <Button variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50">Anterior</Button>
                    <span className="flex items-center px-4 text-sm text-slate-400">Página {page} de {totalPages}</span>
                    <Button variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50">Siguiente</Button>
                  </div>
                )}
              </>
            )}
            
            <RelatedSearches workshopType={workshopType} location={null} filters={filters} />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export function WorkshopsByTypeLocation() {
  const { workshopType, location } = useParams();
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [workshopsRes, filtersRes] = await Promise.all([
          axios.get(`${API}/seo/talleres/${workshopType}/${location}?page=${page}&limit=12`),
          axios.get(`${API}/seo/talleres/filters`)
        ]);
        setWorkshops(workshopsRes.data.workshops || []);
        setTotalPages(workshopsRes.data.pages || 1);
        setTotal(workshopsRes.data.total || 0);
        setData(workshopsRes.data);
        setFilters(filtersRes.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    load();
  }, [workshopType, location, page]);

  // Validar workshopType. Si no es válido → 404 con noindex.
  if (!WORKSHOP_TYPE_LABELS[workshopType]) return <NotFoundPage />;

  const typeLabel = data?.type_label || WORKSHOP_TYPE_LABELS[workshopType] || workshopType;
  const locationLabel = data?.location_label || location;

  const seoTitle = `Talleres de ${typeLabel} en ${locationLabel} | Sin Adicciones`;
  const seoDescription = `${total} talleres de ${typeLabel.toLowerCase()} en ${locationLabel}, Chile. Especializados en rehabilitación de adicciones.`;

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={`https://sinadicciones.org/taller/${workshopType}/${location}`} />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        <link rel="canonical" href={`https://sinadicciones.org/taller/${workshopType}/${location}`} />
      
      <Header />
      <main className="pt-20">
        <section className="bg-slate-900 border-b border-slate-800 py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <Breadcrumbs workshopType={workshopType} typeLabel={typeLabel} location={location} locationLabel={locationLabel} />
            <h1 className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight mb-3" data-testid="workshops-seo-location-title">
              Talleres de {typeLabel} en {locationLabel}
            </h1>
            <p className="text-slate-400 text-lg">
              {total > 0 
                ? `${total} talleres de ${typeLabel.toLowerCase()} en ${locationLabel}` 
                : `Talleres de ${typeLabel.toLowerCase()} en ${locationLabel}`
              }
            </p>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : workshops.length === 0 ? (
              <div className="text-center py-20 bg-slate-900 rounded-xl border border-slate-800">
                <Sparkles className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <h3 className="text-xl text-slate-50 mb-2">No se encontraron talleres de {typeLabel.toLowerCase()} en {locationLabel}</h3>
                <p className="text-slate-400 mb-6">Prueba buscando en otra ciudad</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button asChild variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    <Link to={`/taller/${workshopType}`}>Ver en todo Chile</Link>
                  </Button>
                  <Button asChild variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    <Link to="/talleres">Ver todos los talleres</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="workshops-seo-location-grid">
                  {workshops.map((workshop) => (
                    <WorkshopCard key={workshop.workshop_id} workshop={workshop} />
                  ))}
                </div>
                
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-12">
                    <Button variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50">Anterior</Button>
                    <span className="flex items-center px-4 text-sm text-slate-400">Página {page} de {totalPages}</span>
                    <Button variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50">Siguiente</Button>
                  </div>
                )}
              </>
            )}
            
            <RelatedSearches workshopType={workshopType} location={location} filters={filters} />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
